import React, { Fragment, useContext, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import axios from 'axios'
import { scaleLinear } from 'd3-scale'
import api from '../../Api'
import { convertEnrollmentData } from '../../utils/sites'
import { NavLink, useParams } from 'react-router-dom'
import { StoreContext } from '../../contexts/StoreContext'
import { Grid, Card, CardHeader, CardContent, Input, InputLabel, Slider, Box } from '@material-ui/core'
import { Title, Paragraph } from '../../components/Typography'
import { CircularLoader } from '../../components/Progress/Progress'
import { SitesTable } from '../../components/Tables'
import StudyEnrollment from '../../components/Visualizations/StudyEnrollmentContainer'
import { StudyDemographics } from '../../components/Visualizations/StudyDemographics'
import { SiteContribution } from '../../components/Visualizations/SiteContribution'
import { Milestones } from './Milestones'
import { CombinedMetrics } from './CombinedMetrics'
import { convertBoolToYesOrNo, formatDate } from '../../utils'
import { AuthContext } from '../../contexts'

const useStyles = makeStyles((theme) => ({
  pairStyle: {
    fontSize: '125%',
    display: 'flex',
    marginBottom: '0.5rem',
  },
  keyStyle: {
    color: theme.palette.grey[600],
    marginRight: '0.5rem',
  },
  valueStyle: {
    color: theme.palette.primary.main,
    flex: 1,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
}))

const Key = ({ children }) => {
  const { keyStyle } = useStyles()
  return <span className={keyStyle}>{children}:</span>
}

const Value = ({ children }) => {
  const { valueStyle } = useStyles()
  return <span className={valueStyle}>{children}</span>
}

const dateFields = ['Date Funding was Awarded']

// Profile
const StudyProfile = ({ profile }) => {
  const { pairStyle } = useStyles()

  return (
    <article>
      {Object.keys(profile).map((key, i) => {
        const displayName = profile[key].displayName
        let value = profile[key].value

        if (typeof profile[key].value === 'boolean') {
          value = convertBoolToYesOrNo(profile[key].value)
        } else if (dateFields.includes(key)) {
          value = value ? formatDate(new Date(value)) : 'N/A'
        }

        return (
          <div className={pairStyle} key={i}>
            <Key>{displayName}</Key>
            <Value>{value}</Value>
          </div>
        )
      })}
    </article>
  )
}

export const StudyReportPage = (props) => {
  const { proposalID } = useParams()
  const proposalId = proposalID
  // Use unfiltered proposals - user navigating to /studies/123 should always see it regardless of HEAL filter
  const [store] = useContext(StoreContext)
  const proposals = store.proposals
  const [study, setStudy] = useState(null)
  const [studyProfile, setStudyProfile] = useState(null)
  const [studySites, setStudySites] = useState(null)
  const [studyEnrollmentData, setStudyEnrollmentData] = useState(null)
  const [studyDemographics, setStudyDemographics] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [enrollmentRate, setEnrollmentRate] = useState(0.2)
  const [maxEnrollmentRate, setMaxEnrollmentRate] = useState(2)
  const [initialParticipatingSiteCount, setInitialParticipatingSiteCount] = useState(null)
  const { isPLAdmin } = useContext(AuthContext)

  useEffect(() => {
    if (proposals) {
      try {
        const studyFromRoute = proposals.find((proposal) => proposal.proposalID == proposalId)
        setStudy(studyFromRoute)
      } catch (error) {
        console.log(`Could not load study #${proposalId}`, error)
      }
    }
  }, [proposals])

  useEffect(() => {
    const fetchStudyData = async (proposalID) => {
      await axios
        .all([
          axios.get(api.studyProfile(proposalID), { withCredentials: true }),
          axios.get(api.studySitesByProposalId(proposalID), { withCredentials: true }),
          axios.get(api.studyEnrollmentData(proposalID), { withCredentials: true }),
        ])
        .then(
          axios.spread((profileResponse, sitesResponse, enrollmentResponse) => {
            try{
              let v = profileResponse.data.enrollmentGoal.value
              v = v.replace(",","")
              let parsed_v = parseInt(v)
              profileResponse.data.enrollmentGoal.value = parsed_v
            }
            catch{
                console.log("could not parse int")
            }

            setStudyProfile(profileResponse.data)

            sitesResponse.data.forEach(convertEnrollmentData)

            setStudySites(sitesResponse.data)
            setStudyEnrollmentData(enrollmentResponse.data)
            setInitialParticipatingSiteCount(profileResponse.data.initialParticipatingSiteCount.value)
          })
        )
        .catch((err) => {
          console.log('FAIL', err)
        })
    }
    fetchStudyData(proposalId)
  }, [])

  // Fetched separately (and kept out of the isLoading gate) so a study with no
  // demographics — or an older API without the endpoint — never blocks the report.
  useEffect(() => {
    axios
      .get(api.studyDemographics(proposalId), { withCredentials: true })
      .then((res) => setStudyDemographics(res.data))
      .catch(() => setStudyDemographics([]))
  }, [])

  useEffect(() => {
    setIsLoading(!study || !studyProfile || !studySites || !studyEnrollmentData)
  }, [study, studyProfile, studySites, studyEnrollmentData])

  // Slider
  const handleEnrollmentRateSliderChange = (event, value) => {
    setEnrollmentRate(value)
  }

  const handleEnrollmentRateInputChange = (event) => {
    setEnrollmentRate(event.target.value === '' ? '' : Number(event.target.value))
  }

  const handleEnrollmentRateInputBlur = () => {
    if (enrollmentRate < 0) {
      setEnrollmentRate(0)
    } else if (enrollmentRate > maxEnrollmentRate) {
      setEnrollmentRate(maxEnrollmentRate)
    }
  }

  const handleMaxEnrollmentRateInputChange = (event) => {
    const value = event.target.value === '' ? '' : Number(event.target.value)

    setMaxEnrollmentRate(value)

    if (value && enrollmentRate > value) setEnrollmentRate(value)
  }

  const handleMaxEnrollmentRateInputBlur = () => {
    if (maxEnrollmentRate < 0) {
      setMaxEnrollmentRate(1)
    }
  }

  // Marks for enrollment rate slider
  const sliderScale = scaleLinear()
      .domain([0, maxEnrollmentRate]);

  const marks = sliderScale.ticks(20).map(d => ({ value: d, label: d }));

  return (
    <div>
      <Title>Study Report for {study && (study.shortTitle || '...')}</Title>

      {isLoading && <CircularLoader />}
      {!isLoading && (
        <Grid container spacing={8}>
          <Grid item xs={11} lg={5}>
            <Card style={{ height: '100%' }}>
              <CardHeader title="Study Profile" />
              <CardContent>
                {studyProfile ? (
                  <StudyProfile profile={studyProfile} />
                ) : (
                  <Paragraph>No study profile found.{isPLAdmin && <> Upload it with the <NavLink to="/uploads">Study Profile</NavLink> CSV.</>}</Paragraph>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={11} lg={6}>
            <Milestones
              sites={studySites}
              sitesCount={initialParticipatingSiteCount}
              enrollmentGoal={+studyProfile.enrollmentGoal.value}
              enrollmentData={studyEnrollmentData}
            />
          </Grid>

          <Grid item xs={11}>
            <Card>
              <CardHeader title="Combined Metrics" subheader="Averaged across all study sites" />
              <CardContent>
                {studyProfile ? (
                  <CombinedMetrics study={ study } studyProfile={ studyProfile } sites={ studySites } />
                ) : (
                  <Paragraph>No study profile found.{isPLAdmin && <> Upload it with the <NavLink to="/uploads">Study Profile</NavLink> CSV.</>}</Paragraph>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={11}>
            {studySites && studySites.length > 0 ? (
              // <p></p>
              <SitesTable sites={studySites} title="Sites" paging={true} />
            ) : (
              <Card>
                <CardHeader title="Sites" />
                <CardContent>
                  <Paragraph>
                    No sites list found. Upload it with the <NavLink to="/uploads">Study Sites</NavLink> CSV.
                  </Paragraph>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={11}>
            <Card>
              <CardHeader title="Enrollment Information" />
              <CardContent>
                {studyEnrollmentData && studyEnrollmentData.length > 0 ? (
                  <Fragment>
                    <StudyEnrollment study={study || null} sites={studySites || null} enrollmentRate={enrollmentRate} />
                    <Box mt={3}>
                      <Grid container spacing={6} justify="flex-end">
                        <Grid item xs={5}>
                          <Slider
                            value={enrollmentRate}
                            min={0}
                            max={maxEnrollmentRate}
                            step={0.01}
                            marks={marks}
                            onChange={handleEnrollmentRateSliderChange}
                          />
                        </Grid>
                        <Grid item>
                          <InputLabel htmlFor="enrollment-rate-input">enrollment rate</InputLabel>
                          <Input
                            id="enrollment-rate-input"
                            value={enrollmentRate}
                            margin="dense"
                            onChange={handleEnrollmentRateInputChange}
                            onBlur={handleEnrollmentRateInputBlur}
                            inputProps={{
                              min: 0,
                              max: maxEnrollmentRate,
                              step: 0.01,
                              type: 'number',
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <InputLabel htmlFor="max-enrollment-rate-input">maximum</InputLabel>
                          <Input
                            id="max-enrollment-rate-input"
                            value={maxEnrollmentRate}
                            label="max"
                            margin="dense"
                            onChange={handleMaxEnrollmentRateInputChange}
                            onBlur={handleMaxEnrollmentRateInputBlur}
                            inputProps={{
                              step: 1,
                              min: 1,
                              max: Number.MAX_VALUE,
                              type: 'number',
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fragment>
                ) : (
                  <Paragraph>
                    No enrollment information found. Upload it with the <NavLink to="/uploads">Study Enrollment Data</NavLink> CSV.
                  </Paragraph>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={11}>
            <Card>
              <CardHeader title="Patient Demographics" subheader="Enrollment by ethnicity/race and sex (NIH categories)" />
              <CardContent>
                {studyDemographics && studyDemographics.length > 0 ? (
                  <StudyDemographics demographics={studyDemographics[0]} />
                ) : (
                  <Paragraph>
                    No demographics yet. Upload planned targets with the{' '}
                    <NavLink to="/uploads">Study Profile</NavLink> CSV and actual enrollment with the{' '}
                    <NavLink to="/uploads">Study Sites</NavLink> CSV.
                  </Paragraph>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={11}>
            <Card>
              <CardHeader title="Site Contribution" subheader="Enrollment by site vs. the study goal" />
              <CardContent>
                {studySites && studySites.length > 0 ? (
                  <SiteContribution
                    sites={studySites}
                    enrollmentGoal={studyProfile && studyProfile.enrollmentGoal ? studyProfile.enrollmentGoal.value : 0}
                  />
                ) : (
                  <Paragraph>
                    No site data found. Upload it with the <NavLink to="/uploads">Study Sites</NavLink> CSV.
                  </Paragraph>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </div>
  )
}
